import { PresenceRule } from '@hakimo-ui/hakimo/types';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import { Checkbox } from '@hakimo-ui/shared/ui-base';
import {
  DIRECTION_OPTIONS,
  DirectionOption,
  ENTITY_OPTIONS,
  METHOD_OPTIONS,
} from './ruleSentence';

type LineFields = Pick<PresenceRule, 'direction' | 'entityTypes' | 'methods'>;

interface Props {
  rule: LineFields;
  onChange: (patch: Partial<LineFields>) => void;
}

function toggled<T>(list: T[], item: T, on: boolean): T[] {
  const without = list.filter((x) => x !== item);
  return on ? [...without, item] : without;
}

const LEGEND = 'dark:text-dark-text mb-1 block text-xs text-gray-700';
const HINT = 'text-onlight-text-3 dark:text-ondark-text-3 text-xs';

/** The fields only a line_crossing rule has: direction, who, and how. */
export function LineCrossingFields({ rule, onChange }: Props) {
  return (
    <>
      <div className="space-y-1">
        <SelectMenu<DirectionOption>
          label="Direction"
          value={DIRECTION_OPTIONS.find((d) => d.id === rule.direction)}
          items={DIRECTION_OPTIONS}
          onChange={(option) => onChange({ direction: option.id })}
          displayValue={(item) => item?.label ?? ''}
          id={(item) => item.id}
        />
        <p className={HINT}>
          The arrow on the line shows its direction. Flip the line in the zones
          editor if it points the wrong way.
        </p>
      </div>

      <fieldset className="space-y-1">
        <legend className={LEGEND}>Count</legend>
        <div className="flex gap-6">
          {ENTITY_OPTIONS.map((option) => (
            <Checkbox
              key={option.id}
              label={option.label}
              checked={rule.entityTypes.includes(option.id)}
              onChange={(on) =>
                onChange({
                  entityTypes: toggled(rule.entityTypes, option.id, on),
                })
              }
            />
          ))}
        </div>
        <p className={HINT}>Neither ticked counts both.</p>
      </fieldset>

      <fieldset className="space-y-1">
        <legend className={LEGEND}>A crossing is</legend>
        <div className="space-y-1">
          {METHOD_OPTIONS.map((option) => (
            <div key={option.id}>
              <Checkbox
                label={option.label}
                checked={rule.methods.includes(option.id)}
                onChange={(on) =>
                  onChange({ methods: toggled(rule.methods, option.id, on) })
                }
              />
              <p className={`${HINT} ml-6`}>{option.hint}</p>
            </div>
          ))}
        </div>
        {rule.methods.some((m) => m !== 'observed') && (
          <p className={HINT}>
            Appearing and disappearing are inferences, not sightings. The
            crossings chart shows how often they would have counted here before
            you rely on them.
          </p>
        )}
      </fieldset>
    </>
  );
}

export default LineCrossingFields;
